import { ReorderableList, Typography } from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	PropsWithChildren,
	ReactElement,
	useCallback
} from "react";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { HubDataSource } from "client/Api/Hub/hub";
import { hubDataSourceToLocalizationKeyMap } from "./HubItemNode";
import { HubSidebarItemContent } from "./HubSidebarItemContent";
import { contains } from "client/Data/searching";
import { HubSidebarRootItem } from "./HubSidebarRootItem";

export type HubSidebarTabProps = {
	attachable: IValueInputAttachable<readonly HubDataSource[]>;
	normalizedSearchTerm: string;
	canReorderItems: boolean;
	onItemReorder: () => void;
};

export const RootItemsOrderTabContent = (props: PropsWithChildren<HubSidebarTabProps>): ReactElement => {
	const {
		attachable,
		normalizedSearchTerm,
		canReorderItems,
		onItemReorder
	} = props;

	const overviewText: string = localize("_Overview");
	const shouldShowOverview: boolean = normalizedSearchTerm === ""
		|| contains(
			overviewText,
			normalizedSearchTerm
		);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			onItemReorder();

			const {
				value
			} = attachable;

			const itemsCopy: HubDataSource[] = [...value];
			const item: HubDataSource = itemsCopy.splice(fromIndex, 1)[0];
			itemsCopy.splice(toIndex, 0, item);

			attachable
				.reducer
				.setValue(itemsCopy);

			attachable
				.validation
				.triggerInputFinished();
		},
		[attachable, onItemReorder]
	);

	const getOverlayTextHandler = useCallback(
		(activeIndex: number): string => {
			const activeItem: HubDataSource = ensureNotNil(
				attachable.value[activeIndex]
			);

			return localize(
				hubDataSourceToLocalizationKeyMap[activeItem]
			);
		},
		[attachable]
	);

	return (
		<>
			{shouldShowOverview && (
				<ReorderableList.RowItemLike
					trackingId="overview-item"
					hasSeparator
				>
					<HubSidebarItemContent
						trackingId={generateTrackingId("content")}
					>
						<Typography weight="bold">
							{overviewText}
						</Typography>
					</HubSidebarItemContent>
				</ReorderableList.RowItemLike>
			)}
			<ReorderableList
				onItemMove={canReorderItems ? itemMoveHandler : undefined}
				hasSeparator
				getOverlayText={getOverlayTextHandler}
				trackingId="hub-sidebar-type-item-list"
			>
				{attachable
					.map(
						(x): ReactElement => (
							<HubSidebarRootItem
								key={x.value}
								source={x.value}
							/>
						)
					)
				}
			</ReorderableList>
		</>
	);
};
RootItemsOrderTabContent.displayName = "HubSidebarTab";